// Wire shapes for the record revision-chain endpoints
// (MRE feature/library-30590, `/record-references/*`).

/** One revision of a record, as served by the revision-chain endpoint. */
export interface RevisionInfo {
  /** SourceDataRecord id of this revision (each revision is its own SDR). */
  id: number;
  /** The id of the chain's first revision; null on pre-chain records. */
  chain_id: number | null;
  revision_label: string | null;
  supersedes_fk: number | null;
  /** Null on exactly one revision per chain: the current one. */
  superseded_date: string | null;
  revision_source: string | null;
  create_date: string | null;
}

export interface RevisionChainResponse {
  chain_id: number;
  /** Newest first. A record with no chain returns itself as the only entry. */
  revisions: RevisionInfo[];
}

/**
 * The slice of the SDR wire shape (`schema.SourceDataRecord`) the record view
 * reads. The dataset services are untyped JS, so this is typed here.
 */
export interface RecordInfo {
  id: number;
  source_uid?: string;
  data?: {
    file_name?: string;
    extracted_metadata?: Record<string, unknown>;
    /** The source system's properties, set by edge-agent document sources. */
    source_metadata?: Record<string, unknown>;
    [key: string]: unknown;
  };
  dataset_fk?: number | null;
  project_folder_fk?: number | null;
  create_date?: string;
  last_update_date?: string;
  processing_status?: string | null;
}

/**
 * A record reference: a row making a record appear in a container it does
 * not live in (design spec §2.2). Carries no grants; access resolves against
 * the underlying SDR, so unreadable references are absent from the wire.
 */
export interface RecordReference {
  id: number;
  /** The SDR (revision) the reference holds. */
  sdr_fk: number;
  /** Destination container — exactly one of these is set. */
  dataset_fk: number | null;
  project_folder_fk: number | null;
  added_by_fk: number;
  create_date: string | null;
  /** Move audit (Story 15): set when the reference was repointed. */
  moved_from_sdr_fk: number | null;
  moved_by_fk: number | null;
  moved_at: string | null;
}

export interface RecordReferenceWithRevision extends RecordReference {
  held_revision: RevisionInfo | null;
  /** The chain's current revision when the held one is superseded, else null. */
  newer_revision: RevisionInfo | null;
}

/**
 * A folder-listing row: RecordReferenceWithRevision plus the referenced
 * record's display identity and home container. Only
 * `GET /record-references/folder/{id}` returns these fields.
 */
export interface RecordReferenceDetail extends RecordReferenceWithRevision {
  /** Extracted title, else source_uid, else a fallback naming the SDR. */
  title: string | null;
  file_name: string | null;
  owner: { id: number; email: string; name?: string } | null;
  /** The referenced record's home container, not this reference's own
   * destination (dataset_fk/project_folder_fk above). */
  container_type: "collection" | "project_folder" | null;
  container_id: number | null;
  container_name: string | null;
  /** Null until a separate size stream patches it in. */
  size_bytes: number | null;
}

export interface RecordReferenceListResponse {
  references: RecordReferenceDetail[];
  total: number;
}

/** Exactly one of project_folder_id / dataset_id (1.33 UI only sends folders). */
export interface NewRecordReference {
  sdr_id: number;
  project_folder_id?: number;
  dataset_id?: number;
}

/** A document surfaced by the Related Documents rail tab. */
export interface RelatedRecord {
  sdrId: number;
  title: string;
  fileName: string | null;
  /** Resolved once by the mapper — a hybrid-search hit can be either home. */
  home: RecordHome | undefined;
}

/**
 * RecordHome's discriminant. A real TS `enum`, unlike `PermissionLevel`
 * (`src/api/projectFolders/types.ts`): permission_level arrives as a raw
 * string on the wire, so a nominal enum there would reject a legitimate
 * `"owner"` from an API response. A record's home is never wire data — it's
 * only ever constructed by `recordHome()` or a handful of call sites that
 * already import this module for `RecordHome` itself — so nothing is lost by
 * making a bare `"dataset"` string literal fail to type-check here, and a
 * typo'd or drifted string is caught instead of silently matching.
 */
export enum RecordHomeKind {
  Dataset = "dataset",
  Folder = "folder",
}

/**
 * Where a record lives. The database enforces exactly one scope FK
 * (`ck_sdr_scope_xor` in mre/datamodels.py: both-set and both-null are
 * rejected), so the two-case union is the honest model — two independent
 * optionals would make three of its four states unrepresentable in the data
 * but constructible in the client.
 */
export type RecordHome =
  | { kind: RecordHomeKind.Dataset; datasetId: number }
  | { kind: RecordHomeKind.Folder; folderId: number };

/**
 * The minimal shape recordHome needs. Several producers (citation payloads,
 * provenance references) carry the same two scope FKs under this exact
 * pair — but not a full RecordInfo (no `id`, and often `dataset_id`/
 * `project_folder_id` instead of the `_fk` suffix, mapped in by the caller) —
 * so this is a `Pick`, not `RecordInfo` itself, letting either shape resolve
 * through the same function.
 */
export type RecordHomeSource = Pick<
  RecordInfo,
  "dataset_fk" | "project_folder_fk"
>;

/**
 * The record's home, resolved once so callers don't each pick a precedence.
 * Replicated datasets carry negative ids and the viewer routes expect the
 * positive one, so the dataset id is normalized here.
 */
export const recordHome = (
  record: RecordHomeSource | null | undefined,
): RecordHome | undefined => {
  if (record?.dataset_fk != null)
    return {
      kind: RecordHomeKind.Dataset,
      datasetId: Math.abs(record.dataset_fk),
    };
  if (record?.project_folder_fk != null)
    return {
      kind: RecordHomeKind.Folder,
      folderId: record.project_folder_fk,
    };
  return undefined;
};

/** The record's display title: extracted title if usable, else its filename. */
export const recordTitle = (
  record: RecordInfo | null | undefined,
): string | null => {
  const meta = record?.data?.extracted_metadata as
    | { title?: unknown }
    | undefined;
  const title = typeof meta?.title === "string" ? meta.title.trim() : "";
  return title || record?.data?.file_name || null;
};
